import React from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export const ContactPage = () => {
  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you! We\'ll get back to you soon.');
    e.target.reset();
  };

  return (
    <div className="min-h-screen py-16" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-outfit font-bold text-4xl md:text-5xl mb-4" data-testid="contact-heading">
            Get in Touch
          </h1>
          <p className="text-muted-foreground text-lg">
            Have questions? We'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2 className="font-semibold text-2xl mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" required className="rounded-lg" data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required className="rounded-lg" data-testid="contact-email-input" />
              </div>
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input id="subject" required className="rounded-lg" data-testid="contact-subject-input" />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  rows={5}
                  required
                  className="rounded-lg"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full py-6 bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                data-testid="contact-submit-button"
              >
                Send Message
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="font-semibold text-2xl mb-6">Contact Information</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Email</h3>
                  <p className="text-muted-foreground">support@buggyberry.com</p>
                  <p className="text-muted-foreground">sales@buggyberry.com</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Phone</h3>
                  <p className="text-muted-foreground">+1 (555) 123-4567</p>
                  <p className="text-muted-foreground">Mon-Fri: 9AM - 6PM EST</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Address</h3>
                  <p className="text-muted-foreground">
                    123 Fashion Street<br />
                    New York, NY 10001<br />
                    United States
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">Business Hours</h3>
                  <p className="text-muted-foreground">
                    Monday - Friday: 9AM - 6PM<br />
                    Saturday: 10AM - 4PM<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ Link */}
            <div className="mt-8 p-6 bg-primary/5 rounded-lg">
              <h3 className="font-semibold mb-2">Frequently Asked Questions</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Check out our FAQ section for quick answers to common questions.
              </p>
              <Button variant="outline" className="rounded-full">
                View FAQ
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
